/**
 * Discounts — root-cause analysis for the Revenue Leak signal.
 *
 * NOT a list. NOT a report. Every row answers:
 *   • Where is money leaking?
 *   • Who controls it?
 *   • What do I do next?
 *
 * Companion to Sales Overview. Cross-linked via /discounts?from=revenue_leak.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Lightning, TrendUp, TrendDown, ArrowRight, ArrowLeft, Warning, Info,
  Coin, Storefront, User, Question, Wrench,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Cell,
  Tooltip,
} from 'recharts';
import DateRangeFilter from '../components/DateRangeFilter';
import { resolveInitialRange, isValidISO } from '../lib/dateRange';

// ──────────────────────────────────────────────────────────────────
// Shared design tokens (mirrors Sales Overview)
// ──────────────────────────────────────────────────────────────────

const SEVERITY = {
  good:    { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  warning: { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   dot: 'bg-amber-400' },
  risk:    { text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     dot: 'bg-red-400' },
  neutral: { text: 'text-zinc-300',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/30',    dot: 'bg-zinc-400' },
};

// Per-partner accent colours — same family as Payment Mix.
const PARTNER_COLORS = {
  swiggy_delivery: '#F97316',
  swiggy_dining:   '#FB923C',
  zomato_delivery: '#EF4444',
  zomato_dining:   '#FB7185',
  inhouse_dine_in: '#94A3B8',
  inhouse_takeaway:'#94A3B8',
  inhouse_delivery:'#94A3B8',
};

// Range presets and the date picker now live in `lib/dateRange.js` +
// `components/DateRangeFilter.jsx`. This page used to ship its own
// preset list (30d default, no This/Last Month) — replaced for the
// canonical analytics date contract.

// ──────────────────────────────────────────────────────────────────
// Small components
// ──────────────────────────────────────────────────────────────────

const Card = ({ children, className = '', testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl bg-[#0F1115] border border-white/8 ${className}`}
  >
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-2">
    {children}
  </h3>
);

const StatusPill = ({ severity, children, testid }) => {
  const s = SEVERITY[severity] || SEVERITY.neutral;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase border ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`w-1 h-1 rounded-full ${s.dot}`} />
      {children}
    </span>
  );
};

// ──────────────────────────────────────────────────────────────────
// Source-of-context banner (when arriving from Sales Overview)
// ──────────────────────────────────────────────────────────────────

const SOURCE_COPY = {
  revenue_leak: {
    title: 'You are here because Revenue Leak is high',
    body: (h) => (
      <>You came from Sales Overview because discount rate is{' '}
        <span className="text-red-300 font-mono">{h.rate_pct}% of gross</span>
        {h.delta_pts != null && (
          <>{' '}({h.delta_pts >= 0 ? '↑' : '↓'} {Math.abs(h.delta_pts).toFixed(1)} pts vs prior)</>
        )}.{' '}
        This module shows where the leak is coming from and who controls it.
      </>
    ),
  },
};

const ContextBanner = ({ from, hero, onClose, onBack }) => {
  const cfg = SOURCE_COPY[from];
  if (!cfg || !hero) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="discounts-context-banner"
    >
      <div className="shrink-0 w-7 h-7 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
        <Lightning size={14} weight="fill" className="text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.2em] text-red-400/80 font-mono">Linked from Sales Overview</div>
        <div className="text-sm font-medium text-zinc-100 mt-0.5">{cfg.title}</div>
        <div className="text-xs text-zinc-400 mt-1">{cfg.body(hero)}</div>
      </div>
      <button
        onClick={onBack}
        data-testid="discounts-banner-back-btn"
        className="text-[11px] font-mono text-zinc-300 hover:text-white border border-white/10 rounded-md px-2 py-1 flex items-center gap-1"
      >
        <ArrowLeft size={12} /> Back to Overview
      </button>
      <button
        onClick={onClose}
        data-testid="discounts-banner-dismiss-btn"
        className="text-zinc-500 hover:text-zinc-200 text-lg leading-none"
        aria-label="Dismiss"
      >×</button>
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────────────
// 1. HERO
// ──────────────────────────────────────────────────────────────────

const Hero = ({ hero }) => {
  if (!hero) return null;
  const s = SEVERITY[hero.severity] || SEVERITY.neutral;
  // Handle null delta_pts (unfair comparison mode)
  const hasDelta = hero.delta_pts != null;
  const deltaSign = hasDelta && hero.delta_pts >= 0 ? '+' : '';
  const deltaTone = !hasDelta ? 'text-zinc-500' : hero.delta_pts > 0 ? 'text-red-300' : hero.delta_pts < 0 ? 'text-emerald-300' : 'text-zinc-400';
  return (
    <Card className="p-6" testid="discounts-hero">
      <div className="grid grid-cols-12 gap-6 items-center">
        <div className="col-span-12 md:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Total Discounts</div>
          <div
            className="text-4xl font-bold tabular-nums mt-1 text-zinc-50"
            data-testid="discounts-hero-amount"
          >
            {hero.discount_display}
          </div>
          <div className="text-xs text-zinc-500 font-mono mt-1">{hero.orders} orders</div>
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">% of Gross</div>
          <div
            className={`text-3xl font-bold tabular-nums mt-1 ${s.text}`}
            data-testid="discounts-hero-rate"
          >
            {hero.rate_pct}%
          </div>
          <StatusPill severity={hero.severity}>
            {hero.severity === 'good' ? 'Healthy' : hero.severity === 'warning' ? 'Watch' : 'Risk'}
          </StatusPill>
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">vs Prior Period</div>
          <div className={`text-3xl font-bold tabular-nums mt-1 ${deltaTone}`} data-testid="discounts-hero-delta">
            {hasDelta ? `${deltaSign}${hero.delta_pts.toFixed(1)} pts` : '—'}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5 flex items-center gap-1">
            {!hasDelta ? (
              <span className="text-zinc-500">Comparison not valid</span>
            ) : hero.delta_pts > 0 ? (
              <><TrendUp size={12} className="text-red-400"/> Worsening</>
            ) : hero.delta_pts < 0 ? (
              <><TrendDown size={12} className="text-emerald-400"/> Improving</>
            ) : (
              'Flat'
            )}
          </div>
        </div>
        <div className="col-span-12 md:col-span-2 md:text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Orders Discounted</div>
          <div className="text-2xl font-semibold tabular-nums mt-1 text-zinc-200">
            {hero.discounted_pct}%
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{hero.discount_orders} of {hero.orders}</div>
        </div>
      </div>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// 2. ONLINE vs DIRECT + FIX THIS FIRST
// ──────────────────────────────────────────────────────────────────

const OnlineVsDirect = ({ rows }) => {
  if (!rows?.length) return null;
  return (
    <Card className="p-5 h-full" testid="discounts-online-vs-direct">
      <SectionTitle>Online vs Direct</SectionTitle>
      <div className="space-y-3">
        {rows.map((r, idx) => (
          <div key={r.key}>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-zinc-100">{r.label}</span>
              <span className="text-[11px] text-zinc-500 font-mono">{r.share_pct}% of all discounts</span>
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-2xl font-bold tabular-nums text-zinc-50" data-testid={`discounts-onlinedirect-${r.key}-amount`}>
                {r.amount_display}
              </span>
              <span className={`text-sm font-mono tabular-nums ${r.rate_pct >= 5 ? 'text-red-300' : r.rate_pct >= 2 ? 'text-amber-300' : 'text-emerald-300'}`}>
                {r.rate_pct}% rate
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono mt-1">
              {r.discounted_pct}% of orders ({r.discount_orders}/{r.orders})
            </div>
            {idx === 0 && <div className="border-t border-white/5 mt-3" />}
          </div>
        ))}
      </div>
    </Card>
  );
};

const FixThisFirst = ({ ftf, onViewSourceSignal }) => {
  if (!ftf) return null;
  const s = SEVERITY[ftf.severity] || SEVERITY.warning;
  return (
    <Card className={`p-5 h-full ${s.border}`} testid="discounts-fix-this-first">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${s.dot} animate-pulse`} />
        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-400">Primary Diagnosis</span>
        <StatusPill severity={ftf.severity}>{ftf.case.replace(/_/g, ' ')}</StatusPill>
      </div>
      <h3 className="text-lg font-semibold text-zinc-50 leading-snug" data-testid="discounts-ftf-title">
        {ftf.title}
      </h3>
      <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{ftf.body}</p>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-md bg-zinc-900/60 border border-white/5 px-2 py-1.5">
          <div className="text-zinc-500 font-mono uppercase text-[9px]">Magnitude</div>
          <div className="text-zinc-100 font-semibold">{ftf.primary_metric || '—'}</div>
        </div>
        <div className="rounded-md bg-zinc-900/60 border border-white/5 px-2 py-1.5">
          <div className="text-zinc-500 font-mono uppercase text-[9px]">Trend</div>
          <div className="text-zinc-100 font-semibold">{ftf.secondary_metric || '—'}</div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Action</div>
        <div className="text-sm text-zinc-100 mt-1">{ftf.action}</div>
        {ftf.expected_impact && (
          <div className="text-[11px] text-emerald-300/90 font-mono mt-2">{ftf.expected_impact}</div>
        )}
      </div>

      {onViewSourceSignal && (
        <button
          onClick={onViewSourceSignal}
          data-testid="discounts-ftf-view-signal-btn"
          className="mt-3 text-[11px] font-mono px-3 py-1.5 rounded border border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10 hover:border-white/20 transition-colors inline-flex items-center gap-1.5"
        >
          View in Sales Overview <ArrowRight size={12} />
        </button>
      )}
    </Card>
  );
};

// Banner rendering the comparison-engine verdict (ported from Sales Overview).
const DiscountsComparisonBanner = ({ period }) => {
  if (!period) return null;
  const fairness = period.fairness || {};
  const isPartial = period.current?.is_partial || period.partial;
  const message = fairness.message;
  const basis = fairness.basis;
  if (!message) return null;
  let tone = 'emerald';
  if (basis === 'unequal_period') tone = 'amber';
  else if (basis === 'no_comparison') tone = 'zinc';
  else if (isPartial) tone = 'lime';
  const toneClasses = {
    lime:    { border: 'border-[#D4FF00]/30', bg: 'bg-[#D4FF00]/5',    icon: 'text-[#D4FF00]',   text: 'text-[#E7FF8C]' },
    amber:   { border: 'border-amber-500/30', bg: 'bg-amber-500/10',   icon: 'text-amber-300',   text: 'text-amber-100' },
    zinc:    { border: 'border-white/10',     bg: 'bg-white/[0.02]',   icon: 'text-zinc-400',    text: 'text-zinc-300' },
    emerald: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/5', icon: 'text-emerald-300', text: 'text-emerald-100' },
  }[tone];
  return (
    <div
      className={`rounded-lg border ${toneClasses.border} ${toneClasses.bg} px-3 py-2 text-[11px] font-mono flex items-center gap-2 flex-wrap`}
      data-testid="discounts-comparison-banner"
      data-fairness={fairness.is_fair ? 'fair' : 'unfair'}
      data-basis={basis || 'none'}
    >
      <Warning size={14} className={toneClasses.icon} weight={isPartial || !fairness.is_fair ? 'fill' : 'regular'} />
      <span className={toneClasses.text}>{message}</span>
      {!fairness.is_fair && basis === 'unequal_period' && (
        <span
          className="ml-auto px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/15 text-amber-200 text-[10px] uppercase tracking-wider"
          data-testid="discounts-unfair-chip"
        >
          Scoring uses selected period only
        </span>
      )}
    </div>
  );
};

// Blocked card — shown in place of Primary Diagnosis + Fix-This-First
// whenever the comparison is unfair.
const BlockedDiagnosisCard = ({ onSwitchToAuto }) => (
  <Card className="p-6 h-full border-amber-500/20" testid="discounts-blocked-diagnosis">
    <div className="flex items-center gap-2 mb-3">
      <Warning size={14} className="text-amber-300" weight="fill" />
      <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-amber-200">Comparison not valid</span>
    </div>
    <h3 className="text-base font-semibold text-zinc-50 leading-snug">
      Root-cause analysis is disabled
    </h3>
    <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
      The selected comparison window is not fair for diagnosis. Raw selected-period data remains visible below for
      inspection, but no primary cause, priority list, or action can be inferred from unequal data.
    </p>
    <button
      onClick={onSwitchToAuto}
      data-testid="discounts-switch-to-auto"
      className="mt-3 text-[11px] font-mono px-3 py-1.5 rounded border border-[#D4FF00]/40 bg-[#D4FF00]/10 text-[#D4FF00] hover:bg-[#D4FF00]/20 transition-colors inline-flex items-center gap-1.5"
    >
      Switch to Auto (recommended) <ArrowRight size={12} />
    </button>
  </Card>
);

// Top-3 ranked actions with per-row impact, severity, controllability.
const RankedActionsPanel = ({ actions }) => {
  if (!actions?.length) return null;
  const CTRL_LABEL = { inhouse: 'In-house', staff: 'Staff', aggregator: 'Aggregator', structural: 'Structural' };
  return (
    <Card className="p-5 h-full" testid="discounts-ranked-actions">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-400">Fix This First</span>
        <span className="text-[9px] text-zinc-500 font-mono">top {actions.length} · ranked by impact</span>
      </div>
      <ol className="space-y-3">
        {actions.map((a) => {
          const sev = SEVERITY[a.severity] || SEVERITY.warning;
          return (
            <li
              key={`${a.kind}-${a.rank}-${a.title.slice(0,20)}`}
              className={`rounded-md border ${sev.border} bg-zinc-900/60 px-3 py-2`}
              data-testid={`discounts-ranked-action-${a.rank}`}
              title={`impact_score ${a.impact_score} · ${CTRL_LABEL[a.controllability] || a.controllability}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono text-zinc-500 tabular-nums">#{a.rank}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                <span className="text-[13px] font-medium text-zinc-50 leading-snug flex-1 min-w-0">{a.title}</span>
                <span className="text-[11px] font-mono text-zinc-300 tabular-nums">{a.amount_display}</span>
              </div>
              <div className="text-[11px] text-zinc-400 mt-1 leading-snug">{a.why}</div>
              <div className="text-[11px] text-zinc-200 mt-1.5">
                <span className="text-zinc-500">Action · </span>{a.action}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">
                  {CTRL_LABEL[a.controllability] || a.controllability}
                </span>
                <span className="text-[9px] font-mono text-zinc-600">·</span>
                <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{a.severity}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// 3. PARTNER BREAKDOWN + RATE VIEW
// ──────────────────────────────────────────────────────────────────

const PartnerBars = ({ partners, activePartner, onSelect }) => {
  if (!partners?.length) return null;
  const maxAmt = Math.max(...partners.map((p) => p.amount), 1);
  return (
    <Card className="p-5 h-full" testid="discounts-partner-breakdown">
      <SectionTitle>Partner Breakdown</SectionTitle>
      <div className="text-[10px] text-zinc-500 font-mono mb-3 leading-relaxed">
        Tap a row to filter the entire module to that partner.
      </div>
      <div className="space-y-2.5">
        {partners.map((p) => {
          const isActive = activePartner === p.partner_id;
          return (
            <button
              key={p.partner_id}
              type="button"
              onClick={() => onSelect && onSelect(p.partner_id)}
              data-testid={`discounts-partner-row-${p.partner_id}`}
              className={`w-full text-left rounded-md px-2 py-1.5 transition-colors ${
                isActive
                  ? 'bg-white/10 ring-1 ring-white/20'
                  : 'hover:bg-white/5'
              }`}
            >
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-zinc-200">{p.label}</span>
                <span className="font-mono tabular-nums text-zinc-300">
                  {p.amount_display} <span className="text-zinc-500">({p.share_pct}%)</span>
                </span>
              </div>
              <div className="h-1.5 mt-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(p.amount / maxAmt) * 100}%`,
                    background: PARTNER_COLORS[p.partner_id] || '#94A3B8',
                  }}
                />
              </div>
              <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                {p.rate_pct}% rate · {p.discount_orders}/{p.orders} orders
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const RateView = ({ rateView }) => {
  if (!rateView?.length) return null;
  return (
    <Card className="p-5 h-full" testid="discounts-rate-view">
      <SectionTitle>Highest Discount Rates</SectionTitle>
      <div className="text-[10px] text-zinc-500 font-mono mb-3 leading-relaxed">
        Volume tells you size. Rate tells you margin pressure per ₹.
      </div>
      <div className="space-y-2">
        {rateView.slice(0, 5).map((r) => {
          const s = SEVERITY[r.severity] || SEVERITY.neutral;
          return (
            <div
              key={r.partner_id}
              className="flex items-center justify-between gap-2"
              data-testid={`discounts-rate-row-${r.partner_id}`}
            >
              <span className="text-[12px] text-zinc-200 truncate">{r.label}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-zinc-500 font-mono tabular-nums">{r.amount_display}</span>
                <span className={`px-2 py-0.5 rounded font-mono text-[11px] tabular-nums border ${s.bg} ${s.text} ${s.border}`}>
                  {r.rate_pct}%
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// 4. SERVER LEADERBOARD + DAY OF WEEK
// ──────────────────────────────────────────────────────────────────

const ServerLeaderboard = ({ data, activePartner }) => {
  // Server leaderboard is meaningful only for in-house orders.
  if (activePartner && activePartner !== 'inhouse_dine_in') {
    return (
      <Card className="p-5 h-full" testid="discounts-server-leaderboard">
        <SectionTitle>In-house Discount Contributors</SectionTitle>
        <div className="text-[12px] text-zinc-500 leading-relaxed">
          Server-level discounts apply to in-house orders only.
          Clear the partner filter to see the leaderboard.
        </div>
      </Card>
    );
  }
  if (!data?.rows?.length) {
    return (
      <Card className="p-5 h-full" testid="discounts-server-leaderboard">
        <SectionTitle>In-house Discount Contributors</SectionTitle>
        <div className="text-[12px] text-zinc-500 font-mono">No in-house discounts in this window.</div>
      </Card>
    );
  }
  return (
    <Card className="p-5 h-full" testid="discounts-server-leaderboard">
      <SectionTitle>In-house Discount Contributors</SectionTitle>
      <div className="text-[10px] text-zinc-500 font-mono mb-3 leading-relaxed">
        Where you have direct control. Top 5 servers by discount value.
      </div>
      <div className="space-y-2">
        {data.rows.map((r) => (
          <div
            key={r.server}
            className="flex items-center justify-between gap-3"
            data-testid={`discounts-server-row-${(r.server || 'unknown').replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <User size={12} className="text-zinc-500 shrink-0" />
              <span className="text-[12px] text-zinc-200 truncate">{r.server}</span>
              {r.anomaly && (
                <span
                  className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30"
                  title={`Avg ₹${Math.round(r.avg_per_order)}/discount-order — high per-bill comp`}
                >⚠ high/order</span>
              )}
            </div>
            <div className="flex items-baseline gap-2 shrink-0">
              <span className="text-sm font-mono tabular-nums text-zinc-100">{r.amount_display}</span>
              <span className="text-[10px] text-zinc-500 font-mono">({r.share_pct}%)</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-zinc-400">
        Top contributor: <span className="text-zinc-100 font-semibold">{data.top_share_pct}%</span>{' '}
        of all in-house discount value.
      </div>
    </Card>
  );
};

const DayOfWeek = ({ data }) => {
  if (!data?.rows?.length) return null;
  return (
    <Card className="p-5 h-full" testid="discounts-day-of-week">
      <SectionTitle>Day-of-Week Pattern</SectionTitle>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="day" stroke="#71717A" fontSize={10} />
            <YAxis stroke="#71717A" fontSize={10} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              contentStyle={{ backgroundColor: '#0B0F14', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }}
              formatter={(v, _n, p) => [`${v}% rate · ₹${Number(p.payload.amount).toLocaleString('en-IN')}`, p.payload.day]}
            />
            <Bar dataKey="rate_pct" radius={[4, 4, 0, 0]}>
              {data.rows.map((r) => (
                <Cell
                  key={r.day}
                  fill={r.day === data.peak_day ? '#EF4444' : r.day === data.low_day ? '#10B981' : '#3F3F46'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.insight && (
        <div className="text-[11px] text-zinc-300 font-mono mt-2 pt-2 border-t border-white/5">
          {data.insight}
        </div>
      )}
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// 5. ATTRIBUTION
// ──────────────────────────────────────────────────────────────────

const Attribution = ({ data }) => {
  if (!data) return null;
  const sev = data.quality === 'high' ? 'good' : data.quality === 'medium' ? 'warning' : 'risk';
  const s = SEVERITY[sev];
  return (
    <Card className="p-5 h-full" testid="discounts-attribution">
      <div className="flex items-center justify-between mb-2">
        <SectionTitle>Discount Attribution Quality</SectionTitle>
        <StatusPill severity={sev}>{data.quality}</StatusPill>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase">Named</div>
          <div className="text-2xl font-bold tabular-nums text-emerald-300">{data.named_pct}%</div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase">Unclassified</div>
          <div className={`text-2xl font-bold tabular-nums ${s.text}`}>{data.unnamed_pct}%</div>
        </div>
      </div>
      {/* Stacked bar */}
      <div className="h-2 mt-3 rounded-full overflow-hidden flex bg-white/5">
        <div className="h-full bg-emerald-500/70" style={{ width: `${data.named_pct}%` }} />
        <div className="h-full bg-red-500/40" style={{ width: `${data.unnamed_pct}%` }} />
      </div>
      <div className="text-[11px] text-zinc-400 mt-3 leading-relaxed">
        {data.unnamed_pct >= 50
          ? 'Most discounts lack reason tracking. Add named discount codes in the POS to enable per-campaign attribution.'
          : 'Reason tracking is healthy.'}
      </div>
    </Card>
  );
};

const TopReasons = ({ data }) => {
  if (!data?.top_named?.length) {
    return (
      <Card className="p-5 h-full" testid="discounts-top-reasons">
        <SectionTitle>Top Discount Types</SectionTitle>
        <div className="text-[11px] text-zinc-500 font-mono">No named discount campaigns in this window.</div>
      </Card>
    );
  }
  return (
    <Card className="p-5 h-full" testid="discounts-top-reasons">
      <SectionTitle>Top Discount Types (by name)</SectionTitle>
      <div className="space-y-2">
        {data.top_named.map((n) => (
          <div key={n.name} className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-zinc-200 truncate" title={n.name}>{n.name}</span>
            <span className="text-[12px] font-mono tabular-nums text-zinc-100 shrink-0">{n.amount_display}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────
// MAIN
// ──────────────────────────────────────────────────────────────────

export default function DiscountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // URL-driven date range — shared contract with Sales Overview /
  // Sales Explorer. Default = This Month when URL is missing.
  const initial = resolveInitialRange(searchParams);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [activePartner, setActivePartner] = useState(searchParams.get('partner_id') || null);
  const [comparisonMode, setComparisonMode] = useState(searchParams.get('comparison_mode') || 'auto');

  const sourceFrom = searchParams.get('from'); // 'revenue_leak' etc.

  const params = useMemo(() => {
    const base = { date_from: dateFrom, date_to: dateTo };
    if (activePartner) base.partner_id = activePartner;
    if (comparisonMode) base.comparison_mode = comparisonMode;
    return base;
  }, [dateFrom, dateTo, activePartner, comparisonMode]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    dashboardApi.getDiscounts(params)
      .then((r) => { if (live) { setData(r.data); setError(null); } })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || 'Failed to load discounts'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [JSON.stringify(params)]);

  // Sync URL params (date_from, date_to, partner filter, comparison)
  // — single contract, no legacy `range` param.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('date_from', dateFrom);
    next.set('date_to', dateTo);
    next.delete('range'); // legacy param removed by the canonical contract
    if (activePartner) next.set('partner_id', activePartner);
    else next.delete('partner_id');
    if (comparisonMode && comparisonMode !== 'auto') next.set('comparison_mode', comparisonMode);
    else next.delete('comparison_mode');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, activePartner, comparisonMode]);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  // Toggle partner filter — tapping the same partner clears the filter.
  const handlePartnerSelect = (pid) => {
    setActivePartner((cur) => (cur === pid ? null : pid));
  };
  const activePartnerLabel = useMemo(() => {
    if (!activePartner) return '';
    const found = (data?.partner_breakdown || []).find((p) => p.partner_id === activePartner);
    return found?.label || activePartner;
  }, [activePartner, data]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 p-4 md:p-6" data-testid="discounts-page">
      {/* Header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Discounts</h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Understand where you are losing revenue and why.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter
            value={{ from: dateFrom, to: dateTo }}
            onChange={handleRangeChange}
            testIdPrefix="discounts-range"
          />
          <div className="flex items-center gap-1.5 ml-1" data-testid="discounts-comparison-mode-wrap">
            <span className="hidden md:inline text-[10px] uppercase tracking-widest font-mono text-zinc-500">Compare</span>
            <select
              value={comparisonMode}
              onChange={(e) => setComparisonMode(e.target.value)}
              data-testid="discounts-comparison-mode-select"
              title="Comparison period used for root-cause diagnosis"
              className="text-[11px] font-mono px-2 py-1 rounded border bg-[#0F1115] border-white/10 text-zinc-100 hover:border-white/25"
            >
              <option value="auto">Auto (recommended)</option>
              <option value="previous_period">Previous equal period</option>
              <option value="previous_month_like_for_like">Same days last month</option>
              <option value="previous_month_full">Full previous month</option>
              <option value="previous_year_like_for_like">Same period last year</option>
              <option value="custom">Custom</option>
              <option value="none">No comparison</option>
            </select>
          </div>
        </div>
      </div>

      {/* Context banner ─────────────────────────────────────── */}
      {sourceFrom && !bannerDismissed && data?.hero && (
        <ContextBanner
          from={sourceFrom}
          hero={data.hero}
          onClose={() => setBannerDismissed(true)}
          onBack={() => navigate('/')}
        />
      )}

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 mb-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-zinc-500 text-sm font-mono py-12 text-center">Loading discount intelligence…</div>
      )}

      {data && (
        <div className="space-y-4">
          <DiscountsComparisonBanner period={data.period} />
          {/* Active filter chip — visible whenever a partner is selected. */}
          {activePartner && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="discounts-filter-bar">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-500">Filtered to</span>
              <button
                onClick={() => setActivePartner(null)}
                data-testid="discounts-filter-chip-clear"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-white/15 bg-white/5 text-[11px] font-mono text-zinc-100 hover:bg-white/10"
              >
                {activePartnerLabel}
                <span className="text-zinc-500" aria-hidden>✕</span>
              </button>
              <span className="text-[10px] text-zinc-500 font-mono">— every section below reflects this partner only</span>
            </div>
          )}

          {/* Row 1 — Hero (sticky on mobile so the headline doesn't drift) */}
          <div className="md:static sticky top-0 z-20 -mx-4 md:mx-0 px-4 md:px-0 py-2 md:py-0 bg-[#09090B]/95 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none">
            <Hero hero={data.hero} />
          </div>

          {/* Row 2 — FixThisFirst FIRST on mobile, paired with Online-vs-Direct on desktop. */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6 order-1 md:order-2">
              {((data.period?.fairness?.is_fair ?? true)) ? (
                <FixThisFirst
                  ftf={data.primary_diagnosis || data.fix_this_first}
                  onViewSourceSignal={() => navigate({
                    pathname: '/',
                    search: `?from=discounts_fix_this_first&signal=revenue_leak&date_from=${data.period.date_from}&date_to=${data.period.date_to}`,
                  })}
                />
              ) : (
                <BlockedDiagnosisCard onSwitchToAuto={() => setComparisonMode('auto')} />
              )}
            </div>
            <div className="col-span-12 md:col-span-6 order-2 md:order-1">
              <OnlineVsDirect rows={data.online_vs_direct} />
            </div>
          </div>

          {/* Row 2.5 — Ranked Actions (top 3). Only visible when fair. */}
          {(data.period?.fairness?.is_fair ?? true) && (data.ranked_actions?.length || 0) > 0 && (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                <RankedActionsPanel actions={data.ranked_actions} />
              </div>
            </div>
          )}

          {/* Row 3 — Partner Breakdown + Rate View */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8">
              <PartnerBars
                partners={data.partner_breakdown}
                activePartner={activePartner}
                onSelect={handlePartnerSelect}
              />
            </div>
            <div className="col-span-12 md:col-span-4">
              <RateView rateView={data.rate_view} />
            </div>
          </div>

          {/* Row 4 — Server Leaderboard + Day Pattern */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <ServerLeaderboard data={data.server_leaderboard} activePartner={activePartner} />
            </div>
            <div className="col-span-12 md:col-span-6">
              <DayOfWeek data={data.day_of_week} />
            </div>
          </div>

          {/* Row 5 — Attribution + Top Reasons */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Attribution data={data.attribution} />
            </div>
            <div className="col-span-12 md:col-span-6">
              <TopReasons data={data.attribution} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
